// Sources-tab result card — the feature's OWN Tailwind + logical-RTL card
// (the old mockup `components/SrcCard` was `.tp-*`-styled; F-SOURCES shipped
// its own version per plan §2.3, and the mockup one is now removed).
//
// Pure presentation (W1): every value is supplied by the parent pane, which
// projects the live hook results. The card computes NO citation status (W7):
// `citeDisabled` is passed in (refKey null / insert in flight). The snippet +
// name carry BE-emitted text that may be Arabic inside an FR chrome — they get
// per-string direction via `bidiProps` (CLAUDE.md §9.2 rule 4).

import { useTranslation } from 'react-i18next'
import { bidiProps } from '@/lib/bidi'

/** Display kind — drives the glyph. Mirrors the four groups. */
export type SourceCardKind = 'articles' | 'jurisp' | 'faits' | 'pieces'
/** Jurisprudence qualification (add-in vocabulary). */
export type SourceCardQual = 'supports' | 'nuances' | 'against'

const GLYPH: Record<SourceCardKind, string> = {
  articles: 'A',
  jurisp: 'J',
  faits: 'F',
  pieces: 'P',
}

export interface SourceCardData {
  kind: SourceCardKind
  /** Primary display line (article label / decision case-number / pièce). */
  name: string
  /** Secondary line (corpus + breadcrumb / court / exhibit label / status). */
  meta: string
  /** BE-emitted snippet (may be empty for pièces). */
  snip: string
  /** Only fr/ma have a flag token today; everything else renders none. */
  flag?: 'fr' | 'ma'
  /** ECLI, decisions only. */
  ecli?: string
  /** Qualification badge, decisions only. */
  qual?: SourceCardQual
  /** Disable "+ Citer" — set when the hit has no refKey (not citable) or an
   *  insert is in flight. Faits are always disabled (display-only). */
  citeDisabled?: boolean
  /** Insert this source as a citation chip. Absent → no button (faits). */
  onCite?: () => void
}

export function SourceCard({
  kind,
  name,
  meta,
  snip,
  flag,
  ecli,
  qual,
  citeDisabled,
  onCite,
}: SourceCardData): JSX.Element {
  const { t } = useTranslation()

  return (
    <div
      data-testid={`source-card-${kind}`}
      className="flex flex-col gap-1.5 rounded-md border border-ink/10 bg-[var(--bg-card)] p-2.5"
    >
      {qual && (
        <span
          data-testid="source-card-qual"
          className="inline-flex w-fit items-center gap-1 text-[10px] font-semibold text-ink/70"
        >
          <span aria-hidden="true">●</span>
          {t(`sources.qual.${qual}`)}
        </span>
      )}
      <div className="flex items-start gap-2">
        <span
          aria-hidden="true"
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-ink/5 text-[10px] font-bold text-ink/70"
        >
          {GLYPH[kind]}
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold text-ink" {...bidiProps(name)}>
            {name}
          </div>
          <div className="text-[10px] text-ink/60" {...bidiProps(meta)}>
            {meta}
          </div>
        </div>
        {flag && (
          <span
            aria-hidden="true"
            data-testid={`source-card-flag-${flag}`}
            className="shrink-0 text-[10px] uppercase text-ink/40"
          >
            {flag}
          </span>
        )}
      </div>
      {snip && (
        <div className="text-[11px] leading-relaxed text-ink/80" {...bidiProps(snip)}>
          {snip}
        </div>
      )}
      <div className="flex items-center gap-2">
        {ecli && (
          <span className="font-mono text-[9px] text-ink/40">{ecli}</span>
        )}
        {onCite && (
          <button
            type="button"
            onClick={onCite}
            disabled={citeDisabled}
            className="ms-auto rounded border border-ink/15 px-2 py-0.5 text-[10px] font-semibold text-ink hover:bg-ink/5 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {t('sources.cite')}
          </button>
        )}
      </div>
    </div>
  )
}
